/**
 * PORTED — RO-3-00f / #44473.
 * Origin: `excellent/app/_home/home-screen.tsx`.
 *
 * Cause D rewrite: the Next view assembled Play + attention + portfolio via
 * server modules. This host composes the same surface from live contracts:
 *   - `play.queue`           → Next action
 *   - `suggestions.list`     → Signals
 *   - `onboarding.get`       → greeting display name
 *   - `PortfolioRollup` / `PropertyAnalyticsCard` (this item's A rewrites)
 *
 * PulseSpine claims are decorative here (empty on failure is honest — the
 * wall hero still mounts). GranularPulse is intentionally omitted until its
 * own contract lands; the wall itself is client-fetched by PulseSpine.
 */

import {
  Band,
  Button,
  Card,
  CardContent,
  PageHeader,
  PulseSpine,
  Section,
  formatDate,
  type Intent,
} from "@excellent/ui";
import { RouterLink as Link } from "@excellent/ui/router";
import { useApi } from "@excellent/ui/api";
import { play } from "@excellent/ui/api/contracts/play";
import { suggestions } from "@excellent/ui/api/contracts/suggestions";
import { onboarding } from "@excellent/ui/api/contracts/onboarding";
import { cleanReasons } from "@excellent/ui/blocks/play/shared";
import { SignalsList } from "./signals-list";
import { PortfolioRollup } from "./portfolio-rollup";
import { PropertyAnalyticsCard } from "./property-analytics-card";

const SEVERITY_INTENT: Record<1 | 2 | 3, Intent> = {
  3: "danger",
  2: "warning",
  1: "info",
};

function toTitleCase(s: string): string {
  return s
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function HomeScreen({
  workspaceId = 1,
  project = null,
}: {
  workspaceId?: number;
  project?: { id: number; slug: string; name: string } | null;
}) {
  const { data: playData } = useApi(play.queue, {});
  const { data: suggestionData } = useApi(suggestions.list, {});
  const { data: onboardingData } = useApi(onboarding.get, {});

  const displayName = onboardingData?.state?.display_name ?? null;
  const projectLabel = project ? toTitleCase(project.name) : null;
  const firstName = displayName?.trim().split(/\s+/)[0] ?? null;
  const greeting = firstName
    ? `Welcome back, ${firstName}`
    : (projectLabel ?? "Home");
  const subtitle = projectLabel
    ? `${projectLabel} · ${formatDate(new Date())}`
    : formatDate(new Date());

  const next = playData?.actions?.[0] ?? null;
  const nextWhy = next
    ? (next.body ?? cleanReasons(next)[0] ?? next.payoff ?? null)
    : null;
  const nextHref = next?.ctaHref ?? null;

  const signals = (suggestionData?.attention ?? []).slice(0, 4);
  const signalRows = signals.map((s) => ({
    id: `${s.title}-${s.href ?? ""}`,
    intent: SEVERITY_INTENT[(s.severity ?? 1) as 1 | 2 | 3] ?? "info",
    title: s.title,
    subtitle: s.body ?? undefined,
    href: s.href ?? null,
    cta: "Open",
  }));

  return (
    <div className="mx-auto w-full max-w-6xl px-6 py-8">
      <Band>
        <PageHeader title={greeting} description={subtitle} />

        <PulseSpine
          altitude="aggregate-workspace"
          scope="workspace"
          claims={[]}
          hideAct
        >
          {project ? (
            <PropertyAnalyticsCard
              workspaceId={workspaceId}
              projectId={project.id}
            />
          ) : (
            <PortfolioRollup workspaceId={workspaceId} />
          )}
        </PulseSpine>

        <Section label="Next">
          {next && nextHref ? (
            <Card className="rg-hang">
              <CardContent className="flex flex-col gap-4 px-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 space-y-1">
                  <p className="type-body font-medium text-foreground">
                    {next.title}
                  </p>
                  {nextWhy ? (
                    <p className="type-body text-muted-foreground">{nextWhy}</p>
                  ) : null}
                </div>
                <Button asChild className="shrink-0 self-start sm:self-auto">
                  <Link href={nextHref}>
                    {next.ctaLabel ?? "Open"}
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <p className="type-body text-muted-foreground">
              Nothing queued — you&rsquo;re clear for now.
            </p>
          )}
        </Section>

        <Section label="Signals" aside="ordered by urgency">
          <Card className="gap-0 overflow-hidden py-0">
            <SignalsList rows={signalRows} />
          </Card>
        </Section>
      </Band>
    </div>
  );
}
